import React from 'react'
import { useInspectorContext, useUserColorFormat } from '../../hooks/inspector'
import {
  isArtboardElement,
  SketchArtboardElement,
  SketchLayerElement,
  SketchElement,
  isPageElement,
  ElementType,
  isLayerWithSharedStyle,
} from '../../../../inspector'

import {
  PositionAndSize,
  Overview,
  InspectorSkeleton,
  LayoutSettings,
  GridSettings,
  ArtboardOverview,
  Corners,
  Appearance,
  Text,
  TextContent,
  Style,
  TextOrLayerStyles,
  SymbolInSidebar,
  ExportsFFGate,
} from './sections'
import { SidebarEmpty } from '@sketch/components'

import * as S from './ArtboardDetailInspector.styles'
import {
  useEditableComponentDescription,
  useSetupComponentDescriptions,
} from './useEditableComponentDescription'
import { EditableComponentDescription } from '../../../ComponentsWebView/components/ComponentDescription'
import { ShareWithoutVersion } from '../../../../versioning'
import { VersionFragment } from '@sketch/gql-types'
import { useComponentsTrigger } from '../../../components/ComponentsStateContext'

import { ReactComponent as CodeIcon } from '@sketch/icons/code-block-64'

type ArtboardDetailInspectorProps = {
  share: ShareWithoutVersion
  currentVersion: VersionFragment | undefined
}

/**
 * getInspectedElement
 *
 * This method is in change of deciding what element should be presented on the
 * inspector sidebar
 *
 * Having a selected element always takes precedence because it was a deliberated action
 * from the user
 *
 * If the root element is a artboard meaning we are on the artboard view, and nothing has been
 * selected we show the artboard element, this behaviour exists to prevent layers that have the
 * same size as the artboard to block his selection
 *
 * If we aren't on a artboard root element we should be on a page one, in this case it's normal we default
 * to having the selected element rule
 */
const getInspectedElement = (
  sketchSceneRootElement: SketchElement | null,
  selectedElement: SketchElement | null
) => {
  if (selectedElement) {
    return selectedElement
  }

  if (sketchSceneRootElement && isArtboardElement(sketchSceneRootElement)) {
    return sketchSceneRootElement
  }

  return selectedElement
}

export function ArtboardDetailInspector({
  share,
  currentVersion,
}: ArtboardDetailInspectorProps) {
  const {
    selectedElement,
    sketchSceneRootElement,
    inspectorStatus,
  } = useInspectorContext()

  const inspectedElement = getInspectedElement(
    sketchSceneRootElement,
    selectedElement
  )

  // Start fetching before isInspectorReady is true
  useSetupComponentDescriptions(
    share.identifier,
    inspectedElement,
    currentVersion
  )

  // Trigger async components ingestion
  useComponentsTrigger()

  if (inspectorStatus === 'pending') {
    return <InspectorSkeleton />
  }

  if (inspectorStatus === 'errored') {
    return (
      <S.Container>
        <SidebarEmpty
          title="Inspector failed to load"
          description={
            <>
              We’re looking into the issue. Try again later or visit our{' '}
              <a href="https://status.sketch.com/">Status Page</a>.
            </>
          }
        />
      </S.Container>
    )
  }

  if (!inspectedElement) {
    return (
      <S.Container>
        <SidebarEmpty
          title="Select a layer to inspect it"
          icon={CodeIcon}
          description="Right-click or Control-click on any layer, then select it in the menu."
        />
      </S.Container>
    )
  }

  if (isPageElement(inspectedElement)) {
    throw new Error('Page element not yet supported in inspector')
  }

  return (
    <S.Container>
      {isArtboardElement(inspectedElement) ? (
        <ArtboardElementInfo
          artboardElement={inspectedElement}
          currentVersion={currentVersion}
        />
      ) : (
        <LayerElementInfo
          currentVersion={currentVersion}
          layerElement={inspectedElement}
          shareIdentifier={share.identifier}
        />
      )}
    </S.Container>
  )
}

type ArtboardElementInfoProps = {
  artboardElement: SketchArtboardElement
  currentVersion: VersionFragment | undefined
}

function ArtboardElementInfo({
  artboardElement,
  currentVersion,
}: ArtboardElementInfoProps) {
  const [colorFormat, setColorFormat] = useUserColorFormat()
  const { hasAnyLayerWithExportableAssets } = useInspectorContext()

  const {
    description,
    isEnabled,
    editDescriptionHandler,
  } = useEditableComponentDescription()

  return (
    <>
      <Overview sketchElement={artboardElement} />
      {artboardElement.type === ElementType.SymbolMaster && (
        <EditableComponentDescription
          description={description}
          disabled={!isEnabled}
          onEdit={editDescriptionHandler}
        />
      )}
      <ArtboardOverview
        color={artboardElement.backgroundColor}
        preset={artboardElement.preset}
        frame={artboardElement.rootRelativeBoxRect}
        colorFormat={colorFormat}
        onColorFormatChange={setColorFormat}
      />
      <LayoutSettings layout={artboardElement.layoutSettings} />
      <GridSettings grid={artboardElement.gridSettings} />
      {currentVersion && (
        <S.ExportsWrapper>
          <ExportsFFGate
            exportType="artboard"
            exportFormats={artboardElement.exportFormats}
            hasOtherExportableLayers={hasAnyLayerWithExportableAssets}
            currentVersion={currentVersion}
            exportableAssetId={artboardElement.exportId}
            nodeIdentifier={artboardElement.identifier}
            elementName={artboardElement.name}
          />
        </S.ExportsWrapper>
      )}
    </>
  )
}

type LayerElementInfoProps = {
  layerElement: SketchLayerElement
  currentVersion: VersionFragment | undefined
  shareIdentifier: string
}
function LayerElementInfo({
  layerElement,
  currentVersion,
  shareIdentifier,
}: LayerElementInfoProps) {
  const { hasAnyLayerWithExportableAssets } = useInspectorContext()
  const [colorFormat, setColorFormat] = useUserColorFormat()

  const isDirty =
    layerElement.style && 'isDirty' in layerElement.style
      ? layerElement.style?.isDirty
      : false

  const textOrLayerStyle = isLayerWithSharedStyle(layerElement.style)
    ? layerElement.style.sharedStyleReference
    : undefined

  const hasSymbols =
    layerElement.type === ElementType.SymbolInstance ||
    !!layerElement.parentSymbolLayerElement

  return (
    <>
      <Overview sketchElement={layerElement} />
      <PositionAndSize
        x={layerElement.rootRelativeBoxRect.x}
        y={layerElement.rootRelativeBoxRect.y}
        width={layerElement.layerSize.width}
        height={layerElement.layerSize.height}
        rotate={layerElement.rotation}
      />

      {/* Text or Layer styles */}
      {textOrLayerStyle && currentVersion && (
        <TextOrLayerStyles
          isDirty={isDirty}
          isTextStyleComponent={layerElement.type === ElementType.Text}
          shareIdentifier={shareIdentifier}
          currentVersion={currentVersion}
          sharedStyleReference={textOrLayerStyle}
        />
      )}

      {layerElement.corners && <Corners corners={layerElement.corners} />}
      {layerElement.appearance && (
        <Appearance appearance={layerElement.appearance} />
      )}
      <TextSection sketchElement={layerElement} />
      {layerElement.style && (
        <Style
          style={layerElement.style}
          onColorFormatChange={setColorFormat}
          colorFormat={colorFormat}
          layerType={layerElement.type}
        />
      )}
      {layerElement.text && (
        <TextContent>{layerElement.text.string}</TextContent>
      )}

      {/* Symbols */}
      {hasSymbols && <SymbolInSidebar layerElement={layerElement} />}

      {currentVersion && (
        <S.ExportsWrapper>
          <ExportsFFGate
            exportType="layer"
            exportFormats={layerElement.exportFormats}
            hasOtherExportableLayers={hasAnyLayerWithExportableAssets}
            currentVersion={currentVersion}
            exportableAssetId={layerElement.exportId}
            nodeIdentifier={layerElement.identifier}
            elementName={layerElement.name}
          />
        </S.ExportsWrapper>
      )}
    </>
  )
}

type TextSectionProps = {
  sketchElement: SketchElement
}

function TextSection({ sketchElement }: TextSectionProps) {
  const [colorFormat, setColorFormat] = useUserColorFormat()

  if (sketchElement.type !== ElementType.Text || !sketchElement.text) {
    return null
  }

  const style = sketchElement.style

  return (
    <Text
      attributedString={sketchElement.text}
      verticalAlignment={style.verticalAlignment}
      originalSharedStyleValues={style.originalSharedStyleValues}
      onColorFormatChange={setColorFormat}
      colorFormat={colorFormat}
    />
  )
}
